import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { SearchLogsFilterParams, SearchLogType, SearchLogsListResponse } from '@/types/search-log'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { fetchSearchLogs, fetchSearchLog, deleteSearchLog, bulkDeleteSearchLogs } from '@/lib/api'
import { SortEnum } from '@/enums'

type SearchLogStateType = {
  isLoading: boolean
  response: SearchLogType | null
  error: {
    status?: number
    message: string
  } | null
}

type SearchLogsListStateType = {
  isLoading: boolean
  response: SearchLogsListResponse | null
  error: {
    status?: number
    message: string
  } | null
}

type SearchLogsStoreState = {
  filters: SearchLogsFilterParams
  setFilters: (filters: Partial<SearchLogsFilterParams>) => void
  resetFilters: () => void

  searchLogsListState: SearchLogsListStateType
  fetchSearchLogs: (params?: SearchLogsFilterParams) => Promise<SearchLogsListResponse>
  resetSearchLogsList: () => void

  searchLogState: SearchLogStateType
  getSearchLog: (searchLogId: string) => Promise<SearchLogType>
  deleteSearchLog: (searchLogId: string) => Promise<void>
  resetSearchLog: () => void

  selectedSearchLogIds: string[]
  selectSearchLog: (id: string) => void
  deselectSearchLog: (id: string) => void
  toggleSearchLogSelection: (id: string) => void
  selectAllSearchLogs: (searchLogs: SearchLogType[]) => void
  deselectAllSearchLogs: () => void
  isSearchLogSelected: (id: string) => boolean

  bulkDeleteSearchLogs: (ids: string[]) => Promise<BulkDeleteResponse>
}

const defaultFilters: SearchLogsFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.DESC,
  sortBy: 'createdAt'
}

const initialSearchLogState: SearchLogStateType = {
  isLoading: false,
  response: null,
  error: null
}

const initialSearchLogsListState: SearchLogsListStateType = {
  isLoading: false,
  response: null,
  error: null
}

const useSearchLogsStore = create<SearchLogsStoreState>()(
  devtools((set, get) => ({
    filters: defaultFilters,
    searchLogState: initialSearchLogState,
    searchLogsListState: initialSearchLogsListState,
    selectedSearchLogIds: [],

    setFilters: newFilters =>
      set(state => {
        const updatedFilters = { ...state.filters }
        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined) {
            delete updatedFilters[key as keyof SearchLogsFilterParams]
          } else {
            updatedFilters[key as keyof SearchLogsFilterParams] = value as any
          }
        })
        return { filters: updatedFilters }
      }),

    resetFilters: () => set({ filters: defaultFilters }),

    fetchSearchLogs: async (params?: SearchLogsFilterParams) => {
      const currentState = get().searchLogsListState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        searchLogsListState: { ...initialSearchLogsListState, isLoading: true }
      }))

      try {
        const filtersToUse = params || get().filters
        const data = await fetchSearchLogs(filtersToUse)
        set(() => ({
          searchLogsListState: { ...initialSearchLogsListState, response: data }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          searchLogsListState: {
            ...initialSearchLogsListState,
            error: { status: error.response?.status || 500, message: getErrorMessage(error) }
          }
        }))
        throw error
      }
    },

    resetSearchLogsList: () => {
      set(() => ({ searchLogsListState: initialSearchLogsListState }))
    },

    getSearchLog: async (searchLogId: string) => {
      const currentState = get().searchLogState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        searchLogState: { ...initialSearchLogState, isLoading: true }
      }))

      try {
        const data = await fetchSearchLog(searchLogId)
        set(() => ({
          searchLogState: { ...initialSearchLogState, response: data }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          searchLogState: {
            ...initialSearchLogState,
            error: { status: error.response?.status || 500, message: getErrorMessage(error) }
          }
        }))
        throw error
      }
    },

    deleteSearchLog: async (searchLogId: string) => {
      set(() => ({
        searchLogState: { ...get().searchLogState, isLoading: true }
      }))

      try {
        await deleteSearchLog(searchLogId)
        set(() => ({ searchLogState: initialSearchLogState }))
      } catch (error: any) {
        set(() => ({
          searchLogState: {
            ...initialSearchLogState,
            error: { status: error.response?.status || 500, message: getErrorMessage(error) }
          }
        }))
        throw error
      }
    },

    resetSearchLog: () => {
      set(() => ({ searchLogState: initialSearchLogState }))
    },

    selectSearchLog: id => {
      set(state => {
        if (state.selectedSearchLogIds.includes(id)) {
          return state
        }
        return { selectedSearchLogIds: [...state.selectedSearchLogIds, id] }
      })
    },

    deselectSearchLog: id => {
      set(state => ({
        selectedSearchLogIds: state.selectedSearchLogIds.filter(sid => sid !== id)
      }))
    },

    toggleSearchLogSelection: id => {
      set(state => {
        if (state.selectedSearchLogIds.includes(id)) {
          return { selectedSearchLogIds: state.selectedSearchLogIds.filter(sid => sid !== id) }
        }
        return { selectedSearchLogIds: [...state.selectedSearchLogIds, id] }
      })
    },

    selectAllSearchLogs: searchLogs => {
      set(() => ({ selectedSearchLogIds: searchLogs.map(sl => sl.id) }))
    },

    deselectAllSearchLogs: () => {
      set(() => ({ selectedSearchLogIds: [] }))
    },

    isSearchLogSelected: id => {
      return get().selectedSearchLogIds.includes(id)
    },

    bulkDeleteSearchLogs: async ids => {
      set(state => ({
        searchLogState: { ...state.searchLogState, isLoading: true, error: null }
      }))

      try {
        const response = await bulkDeleteSearchLogs({ ids })
        set(state => ({
          searchLogState: { ...state.searchLogState, isLoading: false },
          selectedSearchLogIds: state.selectedSearchLogIds.filter(id => !response.deletedItems.includes(id))
        }))
        return response
      } catch (error: any) {
        set(state => ({
          searchLogState: {
            ...state.searchLogState,
            error: { status: error.response?.status || 500, message: getErrorMessage(error) },
            isLoading: false
          }
        }))
        throw error
      }
    }
  }))
)

export default useSearchLogsStore
